import Vue from 'vue'
import moment from 'moment'
import { Content, Margins, TDocumentDefinitions, TableCell } from 'pdfmake/interfaces'
import { ProductionPrep, ProductionPrepArea, ProductionPrepTask } from '@/models'
import { DATE_TIME_FORMAT } from '@/utils/constants'
import {
  ONGOING_PREP_TASK_STATUSES,
  PREP_TASK_STATUSES,
  countedTasks,
  ongoingSince,
  percent,
  prepCountedTasks,
  prepStateView,
  prepTaskStatusOption,
  prepTasks,
  taskProgress
} from '@/utils/production-prep'

/**
 * Report of a production preparation, for whoever asked for it: its state and every task with
 * its status, when it was set and its note. No request texts, links nor who marked each task.
 */

// Colours of the Bootstrap variants (scss/_variables.scss), the printed ones a little darker to read well on white
const VARIANT_COLORS: { [variant: string]: string } = {
  success: '#5f9f5f',
  warning: '#c9952f',
  primary: '#458db4',
  secondary: '#8a8f94'
}
const TEXT_COLOR = '#434343'
const MUTED_COLOR = '#8a8f94'
const LINE_COLOR = '#e3e5e8'
const PAGE_WIDTH = 515
const BAR_HEIGHT = 6
// Space above a note, under the text it belongs to
const NOTE_MARGIN: Margins = [0, 2, 0, 0]

const colorOf = (variant: string): string => VARIANT_COLORS[variant] || MUTED_COLOR

/** Progress bar drawn with two rectangles */
const progressBar = (value: number, color: string, width = PAGE_WIDTH): Content => ({
  canvas: [
    { type: 'rect', x: 0, y: 0, w: width, h: BAR_HEIGHT, r: BAR_HEIGHT / 2, color: LINE_COLOR },
    ...(value ? [{ type: 'rect' as const, x: 0, y: 0, w: width * value / 100, h: BAR_HEIGHT, r: BAR_HEIGHT / 2, color }] : [])
  ]
})

const statusText = (vm: Vue, task: ProductionPrepTask): TableCell => {
  const option = prepTaskStatusOption(task.status)
  return { text: vm.$t(option.labelKey).toString(), color: colorOf(option.variant), bold: true, noWrap: true }
}

const taskCell = (vm: Vue, task: ProductionPrepTask): TableCell => ({
  stack: [
    {
      text: [
        task.name,
        task.required ? '' : { text: `  ${vm.$t('production_prep.detail.optional')}`, color: MUTED_COLOR, fontSize: 8 }
      ]
    },
    ...(task.notes ? [{ text: task.notes, color: MUTED_COLOR, italics: true, fontSize: 9, margin: NOTE_MARGIN }] : [])
  ]
})

/** When the status was set; a task never marked has none */
const dateCell = (task: ProductionPrepTask): TableCell => ({
  text: task.statusUpdatedOn ? moment(task.statusUpdatedOn).format(DATE_TIME_FORMAT) : '',
  color: MUTED_COLOR,
  fontSize: 9,
  noWrap: true
})

// Columns of the area tables: status, task (with its note) and when the status was set
const AREA_TABLE_WIDTHS = [70, '*', 70]

/**
 * An area as a table whose first row is its title: the title stays with the first tasks, it is
 * repeated when the area goes on in the next page and a task is never split between pages
 */
function areaSection(vm: Vue, area: ProductionPrepArea): Content {
  const { applicable, done } = taskProgress(countedTasks(area))
  const summary = applicable ? `${done}/${applicable}` : vm.$t('production_prep.status.NOT_APPLICABLE').toString()
  const title: TableCell[] = [{
    colSpan: AREA_TABLE_WIDTHS.length,
    columns: [
      { text: area.name, style: 'area' },
      { text: summary, width: 'auto', color: MUTED_COLOR, margin: [0, 2, 0, 0] }
    ],
    margin: [0, 10, 0, 0]
  }, {}, {}]
  const rows: TableCell[][] = area.tasks.length
    ? area.tasks.map(task => [statusText(vm, task), taskCell(vm, task), dateCell(task)])
    : [[{ text: vm.$t('production_prep.detail.no_tasks').toString(), color: MUTED_COLOR, colSpan: AREA_TABLE_WIDTHS.length }, {}, {}]]
  return {
    table: {
      widths: AREA_TABLE_WIDTHS,
      headerRows: 1,
      keepWithHeaderRows: 1,
      dontBreakRows: true,
      body: [title, ...rows]
    },
    layout: {
      // Lines between the tasks only, not above nor under the title
      hLineWidth: (index: number) => index <= 1 ? 0 : 0.5,
      vLineWidth: () => 0,
      hLineColor: () => LINE_COLOR,
      paddingLeft: () => 0,
      paddingTop: () => 4,
      paddingBottom: () => 4
    }
  }
}

/** In progress or waiting for someone else, with since when: what the report reader cares most about */
function ongoingSection(vm: Vue, prep: ProductionPrep): Content[] {
  const ongoing = prep.areas.reduce((all: { area: string; task: ProductionPrepTask }[], area) => all.concat(
    area.tasks.filter(task => ONGOING_PREP_TASK_STATUSES.includes(task.status)).map(task => ({ area: area.name, task }))
  ), [])
  if (!ongoing.length) return []
  return [{
    table: {
      widths: ['*'],
      body: [[{
        stack: [
          { text: vm.$t('production_prep.report.ongoing').toString(), style: 'area', margin: [0, 0, 0, 4] },
          ...ongoing.map(({ area, task }) => ({
            columns: [
              { text: [{ text: `${area} · `, color: MUTED_COLOR }, task.name] },
              {
                text: ongoingSince(vm, task.status, task.statusUpdatedOn),
                width: 'auto',
                color: colorOf(prepTaskStatusOption(task.status).variant)
              }
            ],
            margin: [0, 2, 0, 0]
          } as Content))
        ],
        fillColor: '#f7f8f9',
        margin: [6, 6, 6, 6]
      }]]
    },
    layout: 'noBorders',
    margin: [0, 14, 0, 0]
  }]
}

function documentDefinition(vm: Vue, prep: ProductionPrep): TDocumentDefinitions {
  const { applicable, done } = taskProgress(prepCountedTasks(prep))
  const state = prepStateView(vm, applicable, done)
  const progress = percent(done, applicable)
  const counters = PREP_TASK_STATUSES
    .map(option => ({ option, count: prepTasks(prep).filter(task => task.status === option.value).length }))
    .filter(({ count }) => count)
  const generatedOn = moment().format(DATE_TIME_FORMAT)

  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 50],
    info: { title: `${vm.$t('production_prep.report.title')} - ${prep.name}` },
    defaultStyle: { fontSize: 10, color: TEXT_COLOR },
    styles: {
      area: { fontSize: 11, bold: true }
    },
    footer: (page: number, pages: number) => ({
      columns: [
        { text: vm.$t('production_prep.report.generated', { date: generatedOn }).toString() },
        { text: `${page} / ${pages}`, alignment: 'right' }
      ],
      fontSize: 8,
      color: MUTED_COLOR,
      margin: [40, 20, 40, 0]
    }),
    content: [
      { text: vm.$t('production_prep.report.title').toString().toUpperCase(), fontSize: 8, color: MUTED_COLOR, characterSpacing: 1 },
      { text: prep.name, fontSize: 18, bold: true, margin: [0, 2, 0, 0] },
      {
        text: `${vm.$t('production_prep.fields.editorial_project')}: ${prep.editorialProjectName || vm.$t('production_prep.detail.no_project')}`,
        color: MUTED_COLOR
      },
      ...(prep.notes ? [{ text: prep.notes, color: MUTED_COLOR, fontSize: 9, margin: NOTE_MARGIN }] : []),
      {
        columns: [
          { text: state.label, bold: true, fontSize: 13, color: colorOf(state.variant) },
          { text: `${progress}%`, bold: true, fontSize: 13, width: 'auto' }
        ],
        margin: [0, 16, 0, 4]
      },
      progressBar(progress, colorOf(state.variant)),
      {
        text: vm.$t('production_prep.detail.progress', { resolved: done, total: applicable }).toString(),
        color: MUTED_COLOR,
        fontSize: 9,
        margin: [0, 4, 0, 0]
      },
      {
        text: counters.reduce((parts: Content[], { option, count }, index) => parts.concat(
          index ? [{ text: '   ·   ', color: MUTED_COLOR }] : [],
          [{ text: `${vm.$t(option.labelKey)} `, color: colorOf(option.variant) }, { text: String(count), bold: true }]
        ), []),
        fontSize: 9,
        margin: [0, 2, 0, 0]
      },
      ...ongoingSection(vm, prep),
      ...prep.areas.map(area => areaSection(vm, area))
    ]
  }
}

/** File name of the report: the preparation name and the day, without characters a file can't have */
const reportFileName = (vm: Vue, prep: ProductionPrep): string =>
  `${vm.$t('production_prep.report.file_name')}-${prep.name}-${moment().format('YYYY-MM-DD')}`
    .replace(/[\\/:*?"<>|\s]+/g, '-') + '.pdf'

/** Builds the report and downloads it. pdfmake and its fonts are only loaded the first time */
export async function downloadPrepReport(vm: Vue, prep: ProductionPrep): Promise<void> {
  const [{ default: pdfMake }, fonts] = await Promise.all([
    import(/* webpackChunkName: "pdfmake" */ 'pdfmake/build/pdfmake'),
    import(/* webpackChunkName: "pdfmake" */ 'pdfmake/build/vfs_fonts')
  ])
  // vfs_fonts sets pdfMake.vfs on its module (this.pdfMake), which webpack exposes as its default export
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fontModule = fonts as any
  pdfMake.vfs = (fontModule.pdfMake || (fontModule.default && fontModule.default.pdfMake) || fontModule).vfs
  pdfMake.createPdf(documentDefinition(vm, prep)).download(reportFileName(vm, prep))
}
